// Analytics result column -> dashboard scope dim.
//
// Apart from analyticsTool.js because that reaches analyticsClient -> shell.js,
// which touches `window` at load; these are dependency-free so a Node test can
// import them. Both public functions are re-exported there for existing callers.

// Strict id-style column names → dim. ONLY these are valid sources
// for the scope's id, because the dashboard's deeplinks (and label
// cache) key off entity UUIDs. Name/hostname columns belong in
// LABEL_COLS_BY_DIM below — they enrich the chip's text but never
// become the link target.
//
// Keys are normalised (lowercased, separators stripped), so
// `endpoint_id`, `endpointId`, and `endpoint.id` all map identically.
const ID_COL_TO_DIM = {
    endpoint: "endpoints",
    endpointid: "endpoints",
    endpointuuid: "endpoints",
    cluster: "clusters",
    clusterid: "clusters",
    clusteruuid: "clusters",
    session: "sessions",
    sessionid: "sessions",
    notificationid: "sessions",
    agent: "agents",
    agentid: "agents",
    agentuuid: "agents",
    model: "models",
    modelid: "models",
    tool: "tools",
    toolid: "tools",
    file: "files",
    fileid: "files",
};

// Name/label-style columns mapped to the dim they describe. Used to
// pick a nice display label for the chip when both a UUID and a
// human-readable column come back together. Each list is ordered
// most-preferred first.
const LABEL_COLS_BY_DIM = {
    endpoints: ["hostname", "endpoint_hostname", "endpoint_name", "host"],
    clusters: ["cluster_name", "cluster"],
    sessions: ["session_name"],
    agents: ["provider", "agent_name"],
    models: ["model_name"],
    tools: ["tool_name", "toolcall_name"],
    files: ["file_path", "path"],
};

// Generic fallback label columns — used when no dim-specific label
// is in the result.
const GENERIC_LABEL_COLUMNS = ["name", "label", "title"];

function normaliseColName(name) {
    return String(name || "").toLowerCase().replace(/[_\-.]/g, "");
}

// Public: classify a column name as an id-style source for a dim.
// Returns the dim string (e.g. "endpoints") or null. Used by the
// chat's analytics result table to render id cells as clickable
// deeplinks, and by the prose linkifier to decide which columns
// contribute session ids. Kept aligned with extractScopeLinks so
// chips, cell links and prose links agree on which columns are
// valid scope targets.
export function getScopeDimForColumn(columnName) {
    const raw = String(columnName || "");
    const direct = ID_COL_TO_DIM[normaliseColName(raw)];
    if (direct) return direct;
    // Without output_alias the backend emits `<step_alias>__<column>`
    // (shared-analytics validate.rs), which agent-written specs get - and
    // `s__notification_id` normalises to `snotificationid`, no match. Real
    // column names are the tail, so fall back to it.
    const cut = raw.lastIndexOf("__");
    if (cut <= 0) return null;
    return ID_COL_TO_DIM[normaliseColName(raw.slice(cut + 2))] || null;
}

// Public: pick the best display-label column for a given dim from a
// set of column names. Returns null when no label column is present
// — callers should fall back to the raw id value.
export function pickLabelColumnForDim(dim, columns) {
    if (!dim) return null;
    const set = new Set(columns || []);
    const candidates = [...(LABEL_COLS_BY_DIM[dim] || []), ...GENERIC_LABEL_COLUMNS];
    for (const c of candidates) {
        if (set.has(c)) return c;
    }
    return null;
}
